import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  CardMedia,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { AutoAwesome as GenerateIcon, Refresh as RegenerateIcon, Save as SaveIcon, PostAdd as PostAddIcon, Download as DownloadIcon, Twitter as TwitterIcon, VerifiedUser as VerifiedIcon, AddPhotoAlternate as UploadIcon } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import loggingService from '../services/loggingService';
import imageService from '../services/imageService';
import { useCredits } from '../hooks/useCredits';
import { eventService } from '../services/eventService';
import UpgradeOverlay from '../components/common/UpgradeOverlay';
import postService from '../services/postService';
import AnimatedLogo from '../components/common/AnimatedLogo';
import { styled } from '@mui/material';
import { toPng } from 'html-to-image';
import { useRef } from 'react';
import TweetGenerator from '../components/TweetGenerator';
import BoldStatementGenerator from '../components/BoldStatementGenerator';
import { Helmet } from 'react-helmet-async';
import { Toaster, toast } from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const TEMPLATE_SIZES = [
  { value: 'square', label: 'Square', aspect: 1, width: 1080, height: 1080 },
  { value: 'portrait', label: 'Portrait', aspect: 0.8, width: 1080, height: 1350 }
];

const TEMPLATE_THEMES = [
  { value: 'brave', label: 'Brave', background: '#18161B', text: '#ffffff', logo: '#FF4500', secondary: '#FF4500' },
  { value: 'dark-mode', label: 'Dark Mode', background: '#212121', text: '#ffffff', secondary: '#ffffff', logo: '#4890fe' },
  { value: 'light-mode', label: 'Light Mode', background: '#ffffff', text: '#000000', secondary: '#aaaaaa', logo: '#4890fe' },
  { value: 'royal-twilight', label: 'Royal Twilight', background: '#39286E', text: '#ffffff', secondary: '#E99C2E', logo: '#E99C2E' },
  { value: 'ocean-breeze', label: 'Ocean Breeze', background: '#1976d2', text: '#000000', secondary: '#ffffff', logo: '#ffffff' },
  { value: 'sunset-orange', label: 'Sunset Orange', background: '#FF5722', text: '#000000', secondary: '#FFF3E0', logo: '#FFF3E0' },
  { value: 'forest-green', label: 'Forest Green', background: '#2e7d32', text: '#ffffff', secondary: '#ffffff', logo: '#ffffff' },
  { value: 'rose-gold', label: 'Rose Gold', background: '#c2185b', text: '#000000', secondary: '#f8bbd0', logo: '#f8bbd0' },
  { value: 'deep-purple', label: 'Deep Purple', background: '#673AB7', text: '#ffffff', secondary: '#ffffff', logo: '#ffffff' },
  { value: 'teal-mint', label: 'Teal Mint', background: '#009688', text: '#000000', secondary: '#E0F2F1', logo: '#E0F2F1' },
  { value: 'crimson-gold', label: 'Crimson Gold', background: '#B71C1C', text: '#000000', secondary: '#FFF176', logo: '#FFF176' },
  { value: 'midnight-blue', label: 'Midnight Blue', background: '#0D47A1', text: '#ffffff', secondary: '#E3F2FD', logo: '#E3F2FD' },
  { value: 'amber-brown', label: 'Amber Brown', background: '#795548', text: '#ffffff', secondary: '#FFECB3', logo: '#FFECB3' },
  { value: 'slate-gray', label: 'Slate Gray', background: '#455A64', text: '#ffffff', secondary: '#ECEFF1', logo: '#ECEFF1' },
];

const TEMPLATE_THEMES_MAP = TEMPLATE_THEMES.reduce((map, theme) => {
  map[theme.value] = theme;
  return map;
}, {});

const BACKGROUND_STYLES = [
  { 
    value: 'custom-image', 
    label: 'Custom Image',
    preview: (color, secondary, customBgImage) => ({
      backgroundColor: color,
      backgroundImage: customBgImage ? `url(${customBgImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    })
  },
  { 
    value: 'solid', 
    label: 'Solid',
    preview: (color) => ({
      backgroundColor: color
    })
  },
  {
    value: 'yellow-waves',
    label: 'Yellow Waves',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 1600 800'%3E%3Cg %3E%3Cpath fill='${encodeURIComponent(color)}' d='M486 705.8c-109.3-21.8-223.4-32.2-335.3-19.4C99.5 692.1 49 703 0 719.8V800h843.8c-115.9-33.2-230.8-68.1-347.6-92.2C492.8 707.1 489.4 706.5 486 705.8z'/%3E%3Cpath fill='${encodeURIComponent(secondary)}' d='M1600 0H0v719.8c49-16.8 99.5-27.8 150.7-33.5c111.9-12.7 226-2.4 335.3 19.4c3.4 0.7 6.8 1.4 10.2 2c116.8 24 231.7 59 347.6 92.2H1600V0z'/%3E%3Cpath fill='${encodeURIComponent(color)}' d='M478.4 581c3.2 0.8 6.4 1.7 9.5 2.5c196.2 52.5 388.7 133.5 593.5 176.6c174.2 36.6 349.5 29.2 518.6-10.2V0H0v574.9c52.3-17.6 106.5-27.7 161.1-30.9C268.4 537.4 375.7 554.2 478.4 581z'/%3E%3Cpath fill='${encodeURIComponent(secondary)}' d='M0 0v429.4c55.6-18.4 113.5-27.3 171.4-27.7c102.8-0.8 203.2 22.7 299.3 54.5c3 1 5.9 2 8.9 3c183.6 62 365.7 146.1 562.4 192.1c186.7 43.7 376.3 34.4 557.9-12.6V0H0z'/%3E%3Cpath fill='${encodeURIComponent(color)}' d='M181.8 259.4c98.2 6 191.9 35.2 281.3 72.1c2.8 1.1 5.5 2.3 8.3 3.4c171 71.6 342.7 158.5 531.3 207.7c198.8 51.8 403.4 40.8 597.3-14.8V0H0v283.2C59 263.6 120.6 255.7 181.8 259.4z'/%3E%3Cpath fill='${encodeURIComponent(secondary)}' d='M1600 0H0v136.3c62.3-20.9 127.7-27.5 192.2-19.2c93.6 12.1 180.5 47.7 263.3 89.6c2.6 1.3 5.1 2.6 7.7 3.9c158.4 81.1 319.7 170.9 500.3 223.2c210.5 61 430.8 49 636.6-16.6V0z'/%3E%3Cpath fill='${encodeURIComponent(color)}' d='M454.9 86.3C600.7 177 751.6 269.3 924.1 325c208.6 67.4 431.3 60.8 637.9-5.3c12.8-4.1 25.4-8.4 38.1-12.9V0H288.1c56 21.3 108.7 50.6 159.7 82C450.2 83.4 452.5 84.9 454.9 86.3z'/%3E%3Cpath fill='${encodeURIComponent(secondary)}' d='M1600 0H498c118.1 85.8 243.5 164.5 386.8 216.2c191.8 69.2 400 74.7 595 21.1c40.8-11.2 81.1-25.2 120.3-41.7V0z'/%3E%3Cpath fill='${encodeURIComponent(color)}' d='M1397.5 154.8c47.2-10.6 93.6-25.3 138.6-43.8c21.7-8.9 43-18.8 63.9-29.5V0H643.4c62.9 41.7 129.7 78.2 202.1 107.4C1020.4 178.1 1214.2 196.1 1397.5 154.8z'/%3E%3Cpath fill='${encodeURIComponent(secondary)}' d='M1315.3 72.4c75.3-12.6 148.9-37.1 216.8-72.4h-723C966.8 71 1144.7 101 1315.3 72.4z'/%3E%3C/g%3E%3C/svg%3E")`,
      backgroundAttachment: 'fixed',
      backgroundSize: 'cover'
    })
  },
  {
    value: 'wavy-lines',
    label: 'Wavy Lines',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1100' height='132' viewBox='0 0 1000 120'%3E%3Cg fill='none' stroke='${encodeURIComponent(secondary)}' stroke-width='2.5' %3E%3Cpath d='M-500 75c0 0 125-30 250-30S0 75 0 75s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 45c0 0 125-30 250-30S0 45 0 45s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 105c0 0 125-30 250-30S0 105 0 105s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 15c0 0 125-30 250-30S0 15 0 15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500-15c0 0 125-30 250-30S0-15 0-15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 135c0 0 125-30 250-30S0 135 0 135s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3C/g%3E%3C/svg%3E")`,
      backgroundSize: '150% auto',
      backgroundRepeat: 'repeat-y',
      backgroundPosition: 'center'
    })
  }
];

const FONT_OPTIONS = [
  { value: 'system-ui', label: 'System' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Playfair Display, serif', label: 'Playfair' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Roboto, sans-serif', label: 'Roboto' }
];

const LOGO_POSITIONS = [
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'none', label: 'No Logo' }
];

const GenerateImage = ({ 
  isModalMode = false, 
  onImageGenerated, 
  initialPrompt = '', 
  onClose 
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageSize, setImageSize] = useState('square_hd');
  const [postPrompt, setPostPrompt] = useState('');
  const [generatedForPost, setGeneratedForPost] = useState(false);
  const [showUpgradeOverlay, setShowUpgradeOverlay] = useState(false);
  const [postsDialogOpen, setPostsDialogOpen] = useState(false);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [imageStyle, setImageStyle] = useState('');
  const [activeTab, setActiveTab] = useState('standard');
  const [templateText, setTemplateText] = useState('<p>Your thoughts shape your world. Share them one post at a time.</p><p><br></p><p>— Instation</p><p><br></p><p>#TweetStyle #BuildInPublic #DailyThoughts</p>');
  const [selectedTemplateStyle, setSelectedTemplateStyle] = useState('tweet');
  
  // Unified Template State
  const [templateSize, setTemplateSize] = useState('square');
  const [templateStyle, setTemplateStyle] = useState('gradient-linear');
  const [templateFont, setTemplateFont] = useState('system-ui');
  const [templateCustomBgImage, setTemplateCustomBgImage] = useState(null);
  
  // Individual color states for each style
  const [solidColors, setSolidColors] = useState({
    bgColor: '#673AB7',
    patternColor: '#FFFFFF',
    textColor: '#FFFFFF'
  });
  
  const [yellowWavesColors, setYellowWavesColors] = useState({
    bgColor: '#ffaa00',
    patternColor: '#ffff66',
    textColor: '#000000'
  });
  
  const [customImageColors, setCustomImageColors] = useState({
    bgColor: '#673AB7',
    patternColor: '#FFFFFF',
    textColor: '#FFFFFF'
  });
  
  // Add state for the new style
  const [wavyLinesColors, setWavyLinesColors] = useState({
    bgColor: '#000000',
    patternColor: '#555555',
    textColor: '#FFFFFF'
  });
  
  // Default colors for style previews/thumbnails
  const defaultPreviewColors = {
    solid: { bgColor: '#673AB7', patternColor: '#FFFFFF' },
    'yellow-waves': { bgColor: '#ffaa00', patternColor: '#ffff66' },
    'custom-image': { bgColor: '#673AB7', patternColor: '#FFFFFF' },
    'wavy-lines': { bgColor: '#000000', patternColor: '#858585' }
  };
  
  // Get current active colors based on selected style
  const getActiveColors = () => {
    switch(templateStyle) {
      case 'solid':
        return solidColors;
      case 'yellow-waves':
        return yellowWavesColors;
      case 'custom-image':
        return customImageColors;
      case 'wavy-lines':
        return wavyLinesColors;
      default:
        return solidColors;
    }
  };
  
  // Update the appropriate color state based on the active style
  const updateActiveColor = (colorType, value) => {
    switch(templateStyle) {
      case 'solid':
        setSolidColors(prev => ({ ...prev, [colorType]: value }));
        break;
      case 'yellow-waves':
        setYellowWavesColors(prev => ({ ...prev, [colorType]: value }));
        break;
      case 'custom-image':
        setCustomImageColors(prev => ({ ...prev, [colorType]: value }));
        break;
      case 'wavy-lines':
        setWavyLinesColors(prev => ({ ...prev, [colorType]: value }));
        break;
      default:
        break;
    }
  };
  
  // Force re-render when style changes to update color pickers
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Handle style changes
  useEffect(() => {
    // Force re-render to update color pickers with the correct values
    setForceUpdate(prev => prev + 1);
  }, [templateStyle]);
  
  // Tweet-specific State
  const [tweetText, setTweetText] = useState('');
  const [tweetHandle, setTweetHandle] = useState('');
  const [tweetName, setTweetName] = useState('Instation');
  const [tweetProfileImage, setTweetProfileImage] = useState(null);
  const [tweetProfileImagePreview, setTweetProfileImagePreview] = useState('/images/instation-logo.png');
  const [isVerified, setIsVerified] = useState(true);
  const [tweetTimestamp, setTweetTimestamp] = useState('5m ago');
  const [downloadingTweet, setDownloadingTweet] = useState(false);
  const tweetRef = useRef(null);
  const [tweetImageBlob, setTweetImageBlob] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { credits, refetchCredits } = useCredits();
  const [tweetSize, setTweetSize] = useState('square');
  const [tweetBgOption, setTweetBgOption] = useState('white');
  const [tweetCustomBgColor, setTweetCustomBgColor] = useState('#ffffff');
  const previewRef = useRef(null);
  const tweetPreviewRef = useRef(null);
  const tweetGeneratorRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tweetTheme, setTweetTheme] = useState('deep-purple');
  const [tweetStyle, setTweetStyle] = useState('gradient-linear');
  const [customBgImage, setCustomBgImage] = useState(null);
  
  // Statement-specific State
  const [statementText, setStatementText] = useState('');
  const [statementSize, setStatementSize] = useState('square');
  const [statementTheme, setStatementTheme] = useState('brave');
  const [statementFont, setStatementFont] = useState('system-ui');
  const [statementLogoPosition, setStatementLogoPosition] = useState('top-right');
  const [statementCustomLogoImage, setStatementCustomLogoImage] = useState(null);
  const [statementImageBlob, setStatementImageBlob] = useState(null);
  const statementGeneratorRef = useRef(null);

  const IMAGE_STYLES = [
    { value: 'realistic', label: 'Realistic', prompt: 'in realistic style, highly detailed' },
    { value: 'cartoon', label: 'Cartoon', prompt: 'in cartoon style, animated, vibrant' },
    { value: 'watercolor', label: 'Watercolor', prompt: 'in watercolor style, artistic, painted' },
    { value: 'digital-art', label: 'Digital Art', prompt: 'in digital art style, modern, sharp' },
    { value: 'anime', label: 'Anime', prompt: 'in anime style, japanese animation' },
    { value: '3d', label: '3D Render', prompt: 'in 3D rendered style, octane render' },
    { value: 'minimalist', label: 'Minimalist', prompt: 'in minimalist style, simple, clean' },
    { value: 'pop-art', label: 'Pop Art', prompt: 'in pop art style, bold colors' }
  ];

  useEffect(() => {
    const storedPrompt = !isModalMode ? localStorage.getItem('postPrompt') : null;
    if (storedPrompt) {
      setPrompt(storedPrompt);
      setPostPrompt(storedPrompt);
      localStorage.removeItem('postPrompt');
      handleGenerate();
    } else if (isModalMode && initialPrompt) {
      setPrompt(initialPrompt);
    }
  }, [isModalMode, initialPrompt]);

  useEffect(() => {
    if (credits === 0) {
      setShowUpgradeOverlay(true);
    }
  }, [credits]);

  useEffect(() => {
    if (location.state?.autoFill && location.state.topic) {
      setPrompt(location.state.topic);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSizeChange = (event, newSize) => {
    if (newSize !== null) {
      setImageSize(newSize);
    }
  };

  const handleStyleChange = (event) => {
    const selectedStyle = event.target.value;
    setImageStyle(selectedStyle);
    
    let cleanPrompt = prompt;
    IMAGE_STYLES.forEach(style => {
      cleanPrompt = cleanPrompt.replace(style.prompt, '').trim();
    });

    if (selectedStyle) {
      const stylePrompt = IMAGE_STYLES.find(style => style.value === selectedStyle)?.prompt || '';
      setPrompt(`${cleanPrompt} ${stylePrompt}`.trim());
    } else {
      setPrompt(cleanPrompt);
    }
  };

  const handleGenerate = async () => {
    if (!prompt) {
      toast.error('Please enter a prompt');
      return;
    }

    try {
      setLoading(true);
      setGeneratedImage(null);

      // Use imageService with InstaImage API
      const response = await imageService.generateImage(prompt, imageSize);

      if (!isModalMode) {
        await loggingService.logActivity({
          type: 'image_generation',
          details: {
            prompt,
            service: 'instaimage',
            timestamp: new Date().toISOString()
          }
        });
      }

      if (response && response.url) {
        setGeneratedImage(response.url);
        if (!isModalMode) {
          eventService.emit('CREDITS_UPDATED');
        }
        toast.success('Image generated successfully with InstaImage AI');
      } else {
        throw new Error('No image URL received');
      }
    } catch (error) {
      console.error('Error Details:', error);
      if (error?.response?.status === 402 || error?.message?.includes('Insufficient')) {
        setShowUpgradeOverlay(true);
        return;
      }
      toast.error('Failed to generate image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const data = await postService.getPosts();
      setAvailablePosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
    }
  };

  const handleAiImageDownload = async () => {
    if (!generatedImage) return;
    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `instation-image-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('AI image downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const handleSaveImageToPost = async (postId) => {
    try {
      setLoading(true);
      const formData = new FormData();
      let imageBlobToSave = null;

      if (activeTab === 'templates') {
        if (selectedTemplate === 'tweet' && tweetImageBlob) {
          imageBlobToSave = tweetImageBlob;
          formData.append('image', imageBlobToSave, 'tweet-image.png');
        } else if (selectedTemplate === 'statement' && statementImageBlob) {
          imageBlobToSave = statementImageBlob;
          formData.append('image', imageBlobToSave, 'statement-image.png');
        }
      } else if (activeTab === 'standard' && generatedImage) {
        const response = await fetch(generatedImage);
        imageBlobToSave = await response.blob();
        formData.append('image', imageBlobToSave, 'generated-image.jpg');
      } else {
        toast.error('No image data available to save');
        setLoading(false);
        return;
      }

      if (!imageBlobToSave) {
          toast.error('Image data is missing');
          setLoading(false);
          return;
      }
      
      formData.append('postId', postId);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/posts/${postId}/image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setPostsDialogOpen(false);
      setTweetImageBlob(null);
      setStatementImageBlob(null);
      toast.success('Image saved to post successfully');
    } catch (error) {
      console.error('Error saving to post:', error);
      toast.error('Failed to save image to post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTweetImageReadyForSave = (blob) => {
    if (!blob) {
        toast.error('Failed to create image from tweet template.');
        return;
      }
    setTweetImageBlob(blob);
    fetchPosts();
    setPostsDialogOpen(true);
  };

  const handleStatementImageReadyForSave = (blob) => {
    if (!blob) {
      toast.error('Failed to create image from statement template.');
      return;
    }
    setStatementImageBlob(blob);
    fetchPosts();
    setPostsDialogOpen(true);
  };

  const handlePreviewDownloadClick = () => {
    if (activeTab === 'standard') {
      handleAiImageDownload();
    } else if (activeTab === 'templates') {
      if (selectedTemplate === 'tweet' && tweetGeneratorRef.current) {
        tweetGeneratorRef.current.handleDownload();
      } else if (selectedTemplate === 'statement' && statementGeneratorRef.current) {
        statementGeneratorRef.current.handleDownload();
      }
    }
  };

  const handlePreviewSaveClick = () => {
    if (isModalMode && generatedImage) {
      if (onImageGenerated) onImageGenerated(generatedImage);
      if (onClose) onClose();
      return;
    }
    if (activeTab === 'standard') {
      fetchPosts();
      setPostsDialogOpen(true);
    } else if (activeTab === 'templates') {
      if (selectedTemplate === 'tweet' && tweetGeneratorRef.current) {
        tweetGeneratorRef.current.handleSaveToPost(); 
      } else if (selectedTemplate === 'statement' && statementGeneratorRef.current) {
        statementGeneratorRef.current.handleSaveToPost();
      }
    }
  };

  const handleTweetProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setTweetProfileImage(file);
    
    const reader = new FileReader();
    reader.onload = () => {
      setTweetProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleTweetImageDownload = async () => {
    if (!tweetRef.current) return;
    
    try {
      setDownloadingTweet(true);
      
      const dataUrl = await toPng(tweetRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        style: {
          border: '1px solid #eff3f4',
          borderRadius: '8px',
          overflow: 'hidden'
        }
      });
      
      const link = document.createElement('a');
      link.download = `tweet-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success('Tweet image downloaded successfully');
    } catch (error) {
      console.error('Error generating tweet image:', error);
      toast.error('Failed to generate tweet image. Please try again.');
    } finally {
      setDownloadingTweet(false);
    }
  };

  const handleSaveTweetToPost = async (imageBlob) => {
    try {
      setLoading(true);
      
      let blob = imageBlob;
      
      if (!blob) {
        throw new Error('Failed to create image from tweet');
      }
      
      fetchPosts();
      setPostsDialogOpen(true);
      
      setTweetImageBlob(blob);
    } catch (error) {
      console.error('Error preparing tweet image:', error);
      toast.error('Failed to prepare tweet image for saving. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTweetStateChange = (state) => {
    console.log('Tweet state updated:', state);
    setTweetText(state.tweetText);
    setTweetHandle(state.tweetHandle);
    setTweetName(state.tweetName);
    setTweetProfileImagePreview(state.tweetProfileImagePreview);
    setIsVerified(state.isVerified);
    setTweetTimestamp(state.tweetTimestamp);
    setTweetSize(state.tweetSize);
    setTweetTheme(state.selectedTheme);
    setTweetStyle(state.selectedStyle);
    setCustomBgImage(state.customBgImage);
    console.log('Tweet state in parent:', { 
      tweetText: state.tweetText,
      tweetHandle: state.tweetHandle,
      tweetName: state.tweetName,
      tweetProfileImagePreview: state.tweetProfileImagePreview,
      isVerified: state.isVerified,
      tweetTimestamp: state.tweetTimestamp,
      tweetSize: state.tweetSize,
      selectedTheme: state.selectedTheme,
      selectedStyle: state.selectedStyle,
      customBgImage: state.customBgImage
    });
  };

  const handleStatementStateChange = (state) => {
    setStatementText(state.statementText);
    setStatementSize(state.statementSize);
    
    if (state.customBgImage !== templateCustomBgImage) {
      setTemplateCustomBgImage(state.customBgImage);
    }
  };

  useEffect(() => {
    console.log("Tweet state in parent:", { 
      tweetText, tweetHandle, tweetName, tweetProfileImagePreview, isVerified, tweetTimestamp, tweetSize, tweetBgOption
    });
  }, [tweetText, tweetHandle, tweetName, tweetProfileImagePreview, isVerified, tweetTimestamp, tweetSize, tweetBgOption, tweetCustomBgColor]);

  useEffect(() => {
    setTweetText(templateText);
    setStatementText(templateText);
  }, [templateText]);

  const activeTheme = TEMPLATE_THEMES.find(t => t.value === tweetTheme) || TEMPLATE_THEMES[0];

  return (
    <>
      {!isModalMode && (
        <Helmet>
          <title>Generate LinkedIn Images | Instation</title>
          <meta name="description" content="Create professional images for your LinkedIn posts with AI-powered image generation." />
        </Helmet>
      )}
      
      <Toaster position="top-center" />
      <Container maxWidth="xl" className={!isModalMode ? "page-container" : ""} sx={isModalMode ? {pt: 2, height:'100%', display:'flex', flexDirection:'column'} : {}}>
        <Box className={!isModalMode ? "content-container" : ""} sx={isModalMode ? { flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : {}}>
          {showUpgradeOverlay && !isModalMode && (
            <UpgradeOverlay 
              message="You've used all your credits for this month"
              buttonText="Upgrade Now"
              onUpgrade={() => navigate('/dashboard/upgrade')}
            />
          )}
          <Grid container spacing={3} className="content-grid">
            <Grid item xs={12} md={5} lg={4} className="content-panel">
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    color: '#2D3748',
                    fontSize: '1.1rem',
                    fontFamily: 'var(--font-family)',
                    flexShrink: 0,
                  }}
                >
                  Image Settings
                </Typography>

                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    mb: 3,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#4890fe',
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: '#4A5568',
                      '&.Mui-selected': {
                        color: '#4890fe',
                        fontWeight: 600,
                      },
                    },
                    flexShrink: 0,
                  }}
                >
                  <Tab value="standard" label="Standard" />
                  <Tab value="templates" label="Templates" />
                </Tabs>

                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 2,
                    pt: 1,
                  }}
                >
                  {activeTab === 'standard' ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Be descriptive about the image you want to generate..."
                        sx={{ 
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: '#4890fe',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4890fe',
                            }
                          }
                        }}
                      />
                      
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          mb: 2,
                          fontWeight: 600,
                          color: '#4A5568'
                        }}
                      >
                        Image Size
                      </Typography>
                      
                      <ToggleButtonGroup
                        value={imageSize}
                        exclusive
                        onChange={handleSizeChange}
                        aria-label="image size"
                        fullWidth
                        sx={{ 
                          mb: 3,
                          display: 'flex',
                          flexWrap: 'wrap',
                          '& .MuiToggleButton-root': {
                            flexGrow: 1,
                            flexBasis: 'auto',
                            color: '#4A5568',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(72, 144, 254, 0.08)',
                              color: '#4890fe',
                              borderColor: '#4890fe',
                              '&:hover': {
                                backgroundColor: 'rgba(72, 144, 254, 0.12)',
                              },
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          },
                        }}
                      >
                        <ToggleButton 
                          value="square_hd" 
                          aria-label="square HD"
                          sx={{ borderRadius: '8px 0 0 8px' }}
                        >
                          Square HD
                        </ToggleButton>
                        <ToggleButton 
                          value="portrait_4_3" 
                          aria-label="portrait"
                        >
                          Portrait
                        </ToggleButton>
                        <ToggleButton 
                          value="landscape_4_3" 
                          aria-label="landscape"
                          sx={{ borderRadius: '0 8px 8px 0' }}
                        >
                          Landscape
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel sx={{ fontWeight: 500 }}>Image Style</InputLabel>
                        <Select
                          value={imageStyle}
                          onChange={handleStyleChange}
                          label="Image Style"
                          sx={{ 
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.1)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4890fe',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4890fe',
                            }
                          }}
                        >
                          {IMAGE_STYLES.map(style => (
                            <MenuItem 
                              key={style.value} 
                              value={style.value}
                              sx={{ 
                                padding: '12px 16px',
                              }}
                            >
                              {style.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGenerate}
                        disabled={loading || !prompt}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
                        sx={{ 
                          height: 48,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '16px',
                          bgcolor: '#4890fe',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#3a7ad9',
                            boxShadow: 'none'
                          },
                          '&:disabled': {
                            bgcolor: '#e0e0e0'
                          }
                        }}
                      >
                        {loading ? 'Generating...' : (
                          <>
                            Generate Image
                            <Typography component="span" sx={{
                              ml: 1,
                              fontSize: '0.75rem',
                              color: !prompt ? 'rgba(255, 255, 255, 0.5)' : 'white'
                            }}>
                              (1 credit)
                            </Typography>
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <ToggleButtonGroup
                        value={selectedTemplateStyle}
                        exclusive
                        onChange={(e, newValue) => {
                          if (newValue) setSelectedTemplateStyle(newValue);
                        }}
                        aria-label="template style"
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <ToggleButton value="tweet" aria-label="tweet style">
                          Tweet Style
                        </ToggleButton>
                        <ToggleButton value="statement" aria-label="statement style">
                          Bold Statement
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568' }}>Content</Typography>
                      <ReactQuill 
                        theme="snow" 
                        value={templateText} 
                        onChange={setTemplateText}
                        style={{ minHeight: '150px', marginBottom: '16px' }}
                      />
                      
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: '#4A5568' }}>Image Settings</Typography>
                        <ToggleButtonGroup
                          value={templateSize}
                          exclusive
                          onChange={(e, v) => v && setTemplateSize(v)}
                          fullWidth
                          size="small"
                          sx={{ mb: 2.5 }}
                        >
                          {TEMPLATE_SIZES.map(size => (
                            <ToggleButton key={size.value} value={size.value}>
                              {size.label}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                        
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568' }}>Background Style</Typography>
                        
                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <input
                            accept="image/*"
                            id="background-image-upload"
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                setTemplateCustomBgImage(ev.target.result);
                                setTemplateStyle('custom-image');
                              };
                              reader.readAsDataURL(file);
                            }}
                          />
                          <label htmlFor="background-image-upload">
                            <Button component="span" variant="outlined" startIcon={<UploadIcon />} fullWidth>
                              Upload Background Image
                            </Button>
                          </label>
                          {templateCustomBgImage && (
                            <Box sx={{ position: 'relative', height: '100px' }}>
                              <img src={templateCustomBgImage} alt="background preview" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}/>
                            </Box>
                          )}
                        </Box>

                        <Grid container spacing={1} sx={{ mb: 2.5 }}>
                          {BACKGROUND_STYLES.filter(style => style.value !== 'custom-image').map(style => {
                            // Use default colors for the preview thumbnails
                            const previewColors = defaultPreviewColors[style.value] || { bgColor: '#673AB7', patternColor: '#FFFFFF' };
                            
                            return (
                              <Grid item xs={6} sm={3} key={style.value}>
                                <Box
                                  onClick={() => setTemplateStyle(style.value)}
                                  sx={{ cursor: 'pointer', border: `2px solid ${templateStyle === style.value ? '#4890fe' : 'transparent'}`, borderRadius: '8px', overflow: 'hidden' }}
                                >
                                  <Box sx={{ width: '100%', paddingTop: '75%', position: 'relative', ...style.preview(previewColors.bgColor, previewColors.patternColor) }}/>
                                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 0.5 }}>
                                    {style.label}
                            </Typography>
                          </Box>
                              </Grid>
                            );
                          })}
                        </Grid>

                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568' }}>Colors</Typography>
                        <Grid container spacing={2} sx={{ mb: 2.5 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>Background</Typography>
                            <input 
                              key={`bg-color-${templateStyle}-${forceUpdate}`}
                              type="color" 
                              value={getActiveColors().bgColor} 
                              onChange={(e) => updateActiveColor('bgColor', e.target.value)} 
                              style={{ width: '100%', height: '40px' }} 
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>Pattern</Typography>
                            <input 
                              key={`pattern-color-${templateStyle}-${forceUpdate}`}
                              type="color" 
                              value={getActiveColors().patternColor} 
                              onChange={(e) => updateActiveColor('patternColor', e.target.value)} 
                              style={{ width: '100%', height: '40px' }} 
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>Text</Typography>
                            <input 
                              key={`text-color-${templateStyle}-${forceUpdate}`}
                              type="color" 
                              value={getActiveColors().textColor} 
                              onChange={(e) => updateActiveColor('textColor', e.target.value)} 
                              style={{ width: '100%', height: '40px' }} 
                            />
                          </Grid>
                        </Grid>

                        {selectedTemplateStyle === 'statement' && (
                          <>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#4A5568' }}>Font Style</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                              <Select value={templateFont} onChange={(e) => setTemplateFont(e.target.value)}>
                                {FONT_OPTIONS.map(font => (
                                  <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
                                    {font.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        )}
                          </Box>
                          
                      {selectedTemplateStyle === 'tweet' ? (
                            <TweetGenerator
                              ref={tweetGeneratorRef}
                          tweetText={templateText}
                          onTextChange={setTemplateText}
                          hideEditor={true}
                              tweetHandle={tweetHandle}
                              tweetName={tweetName}
                              tweetProfileImagePreview={tweetProfileImagePreview}
                              isVerified={isVerified}
                              tweetTimestamp={tweetTimestamp}
                          tweetSize={templateSize}
                          bgColor={getActiveColors().bgColor}
                          patternColor={getActiveColors().patternColor}
                          textColor={getActiveColors().textColor}
                          selectedStyle={templateStyle}
                          customBgImage={templateCustomBgImage}
                          selectedFont={templateFont}
                              onSaveToPost={handleTweetImageReadyForSave}
                              onStateChange={handleTweetStateChange}
                          sizes={TEMPLATE_SIZES}
                          backgroundStyles={BACKGROUND_STYLES}
                            />
                          ) : (
                            <BoldStatementGenerator
                              ref={statementGeneratorRef}
                          statementText={templateText}
                          onTextChange={setTemplateText}
                          hideEditor={true}
                          statementSize={templateSize}
                          bgColor={getActiveColors().bgColor}
                          patternColor={getActiveColors().patternColor}
                          textColor={getActiveColors().textColor}
                          selectedStyle={templateStyle}
                          customBgImage={templateCustomBgImage}
                          selectedFont={templateFont}
                              onSaveToPost={handleStatementImageReadyForSave}
                              onStateChange={handleStatementStateChange}
                          sizes={TEMPLATE_SIZES}
                          backgroundStyles={BACKGROUND_STYLES}
                            />
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7} lg={8} className="content-panel">
              <Paper elevation={0} className="content-paper">
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  pb: 2,
                  mb: 3,
                  flexShrink: 0,
                }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#2D3748',
                      fontSize: '1.1rem',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    Preview
                  </Typography>

                  <Box sx={{ 
                    display: 'flex',
                    gap: 1,
                    mt: 0,
                    justifyContent: 'flex-end'
                  }}>
                    {activeTab === 'standard' && (
                      <Tooltip title="Regenerate image">
                        <Button
                          variant="outlined"
                          onClick={handleGenerate}
                          size="small"
                          disabled={!generatedImage || loading}
                          sx={{
                            minWidth: '40px',
                            width: '40px',
                            height: '40px',
                            p: 0,
                            borderRadius: 2,
                            borderColor: '#4890fe',
                            color: '#4890fe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              borderColor: '#3a7ad9',
                              bgcolor: 'rgba(72, 144, 254, 0.04)'
                            }
                          }}
                        >
                          <RegenerateIcon fontSize="small" sx={{ display: 'block' }} />
                        </Button>
                      </Tooltip>
                    )}

                    <Tooltip title={activeTab === 'templates' ? "Download image (1 credit)" : "Download image"}>
                      <span>
                        <IconButton
                          onClick={handlePreviewDownloadClick}
                          disabled={
                            loading || 
                            (activeTab === 'standard' && !generatedImage) || 
                            (activeTab === 'templates' && !statementText)
                          }
                          sx={{ color: '#4890fe' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={isModalMode ? "Use This Image" : (activeTab === 'templates' ? "Save to post (1 credit)" : "Save to post")}>
                      <span>
                        <IconButton
                          onClick={handlePreviewSaveClick}
                          disabled={
                            loading || 
                            (activeTab === 'standard' && !generatedImage) || 
                            (activeTab === 'templates' && !statementText && !isModalMode)
                          }
                          sx={{ color: '#28a745' }}
                        >
                          {loading && activeTab === 'templates' && !isModalMode ? (
                            <CircularProgress size={20} sx={{ color: 'inherit' }} /> 
                          ) : ( 
                            isModalMode && generatedImage ? <PostAddIcon fontSize="small" /> : <SaveIcon fontSize="small" /> 
                          )
                          }
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  ref={previewRef}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    border: '2px dashed rgba(0, 0, 0, 0.1)',
                    borderRadius: 2,
                    p: 2,
                    position: 'relative',
                    overflow: 'auto',
                  }}
                >
                  {activeTab === 'standard' ? (
                    generatedImage ? (
                      <motion.img 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        src={generatedImage} 
                        alt="Generated artwork"
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                        }} 
                      />
                    ) : (
                      loading ? (
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%'
                        }}>
                          <AnimatedLogo size={100} color="#4890fe" sx={{ mb: 3 }} />
                          <Typography variant="h6" sx={{ color: '#2D3748', fontSize: '1.1rem', fontFamily: 'var(--font-family)' }}>
                            Generating your image...
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            This may take a few seconds
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box 
                            sx={{ 
                              width: 120,
                              height: 120,
                              borderRadius: '50%',
                              bgcolor: 'rgba(72, 144, 254, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 3
                            }}
                          >
                            <GenerateIcon sx={{ fontSize: 48, color: '#4890fe' }} />
                          </Box>
                          <Typography 
                            variant="h6"
                            sx={{ 
                              color: '#2D3748',
                              fontWeight: 600,
                              mb: 1,
                              fontSize: '1.1rem',
                              fontFamily: 'var(--font-family)'
                            }}
                          >
                            Ready to generate an image?
                          </Typography>
                          <Typography 
                            variant="body2"
                            sx={{ 
                              color: '#4A5568',
                              maxWidth: 400,
                              textAlign: 'center'
                            }}
                          >
                            Fill in the form on the left with a detailed description and click<br />
                            "Generate Image"
                          </Typography>
                        </Box>
                      )
                    )
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      padding: 2
                    }}>
                      {selectedTemplateStyle === 'tweet' ? (
                        <TweetGenerator
                          tweetText={templateText}
                          tweetHandle={tweetHandle}
                          tweetName={tweetName}
                          tweetProfileImagePreview={tweetProfileImagePreview}
                          isVerified={isVerified}
                          tweetTimestamp={tweetTimestamp}
                          tweetSize={templateSize}
                          bgColor={getActiveColors().bgColor}
                          patternColor={getActiveColors().patternColor}
                          textColor={getActiveColors().textColor}
                          selectedStyle={templateStyle}
                          customBgImage={templateCustomBgImage}
                          selectedFont={templateFont}
                          previewRef={tweetPreviewRef}
                          previewOnly={true}
                          sizes={TEMPLATE_SIZES}
                          backgroundStyles={BACKGROUND_STYLES}
                        />
                      ) : selectedTemplateStyle === 'statement' ? (
                        <BoldStatementGenerator
                          statementText={templateText}
                          statementSize={templateSize}
                          bgColor={getActiveColors().bgColor}
                          patternColor={getActiveColors().patternColor}
                          textColor={getActiveColors().textColor}
                          selectedStyle={templateStyle}
                          customBgImage={templateCustomBgImage}
                          selectedFont={templateFont}
                          previewOnly={true}
                          sizes={TEMPLATE_SIZES}
                          backgroundStyles={BACKGROUND_STYLES}
                        />
                      ) : (
                        <Box sx={{ 
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="body1">
                            Select a template to get started
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {postsDialogOpen && !isModalMode && (
            <Dialog 
              open={postsDialogOpen} 
              onClose={() => setPostsDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              sx={{
                '& .MuiDialog-paper': {
                  borderRadius: 2,
                  overflow: 'hidden'
                }
              }}
            >
              <DialogTitle sx={{ 
                bgcolor: '#f8fafc',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                fontWeight: 600
              }}>
                Select Post
              </DialogTitle>
              <DialogContent>
                <List>
                  {availablePosts.map((post) => (
                    <ListItem key={post.id} disablePadding>
                      <ListItemButton onClick={() => handleSaveImageToPost(post.id)}>
                        <ListItemText 
                          primary={post.topic || 'Untitled Post'} 
                          secondary={post.content}
                          secondaryTypographyProps={{
                            noWrap: true,
                            style: { maxWidth: '100%' }
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {availablePosts.length === 0 && (
                    <ListItem>
                      <ListItemText 
                        primary="No posts available" 
                        secondary="Create a post first to save images"
                      />
                    </ListItem>
                  )}
                </List>
              </DialogContent>
            </Dialog>
          )}
        </Box>
      </Container>
    </>
  );
};

export default GenerateImage; 